"use client";

import { DefaultItem, ListBox, type ItemBase } from "@ui/components/core/ListBox";
import { cva } from "class-variance-authority";
import { clsx } from "clsx";
import Image from "next/image";
import { memo } from "react";
import { Button, SelectProps, SelectValue, Select as RACSelect } from "react-aria-components";

export type SelectRedesignVariant = "default" | "pop-image";

const triggerVariants = cva(
  "tw-w-full tw-font-medium tw-text-black tw-flex tw-items-center tw-outline-none tw-transition-colors",
  {
    variants: {
      variant: {
        default: "tw-text-sm/sm tw-gap-2 tw-h-9 tw-rounded-xl tw-pl-3 tw-pr-2",
        "pop-image": "tw-text-base/none tw-gap-2 tw-h-10 tw-rounded-full tw-py-0 tw-pl-2.5 tw-pr-2 hover:tw-bg-gray-50",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function PopImageItem({
  item,
  hideLabelOnMobile = false,
  hideImageOnMobile = false,
  selected = false,
}: {
  item: ItemBase;
  hideLabelOnMobile?: boolean;
  hideImageOnMobile?: boolean;
  selected?: boolean;
}) {
  const label = item.label || item.textValue;

  return (
    <div className='tw-relative tw-flex tw-min-w-0 tw-items-center tw-gap-2'>
      {item.image && (
        <span
          className={clsx(
            "tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-white tw-ring-1 tw-ring-black/10",
            selected ? "tw-size-10 tw-shadow-[0_0_0.5px_rgb(0_0_0/0.4),0_4px_2px_rgb(0_0_0/0.04)]" : "tw-size-7",
            hideImageOnMobile && "max-sm:tw-hidden",
          )}
        >
          <Image
            sizes='1.75rem'
            className={clsx("tw-rounded-full tw-object-cover", selected ? "tw-size-7" : "tw-size-full")}
            alt=''
            {...item.image}
          />
        </span>
      )}
      <span
        className={clsx(
          "tw-text-black",
          selected ? "tw-whitespace-nowrap tw-text-base/none tw-font-normal" : "tw-truncate tw-text-sm/sm",
          hideLabelOnMobile && "max-sm:tw-hidden",
        )}
      >
        {label}
      </span>
    </div>
  );
}

export type SelectRedesignProps<T extends ItemBase> = Omit<SelectProps<T>, "children"> & {
  defaultValue?: string;
  listBoxClassName?: string;
  listBoxStyle?: React.CSSProperties;
  hideSelectedLabelOnMobile?: boolean;
  hideSelectedImageOnMobile?: boolean;
  variant?: SelectRedesignVariant;
} & Pick<React.ComponentProps<typeof ListBox<T>>, "virtualize" | "items" | "scrollTo">;

export const SelectRedesign = memo(function SelectRedesign<T extends ItemBase>({
  items,
  virtualize,
  listBoxClassName,
  listBoxStyle,
  scrollTo,
  hideSelectedLabelOnMobile = false,
  hideSelectedImageOnMobile = false,
  variant = "default",
  ...props
}: SelectRedesignProps<T>) {
  return (
    <RACSelect {...props}>
      {({ isOpen }) => (
        <>
          <Button
            className={({ isFocusVisible }) =>
              clsx(triggerVariants({ variant }), isFocusVisible ? "tw-outline-focus" : "tw-outline-none")
            }
          >
            {({ isHovered }) => (
              <>
                <SelectValue<T> className='tw-min-w-0 tw-flex-1'>
                  {({ selectedItems }) =>
                    selectedItems[0] ? (
                      variant === "pop-image" ? (
                        <PopImageItem
                          item={selectedItems[0]}
                          hideLabelOnMobile={hideSelectedLabelOnMobile}
                          hideImageOnMobile={hideSelectedImageOnMobile}
                          selected
                        />
                      ) : (
                        <DefaultItem
                          item={selectedItems[0]}
                          showSubLabel={false}
                          labelClassName={hideSelectedLabelOnMobile ? "max-sm:tw-hidden" : undefined}
                          imageClassName={hideSelectedImageOnMobile ? "max-sm:tw-hidden" : undefined}
                        />
                      )
                    ) : null
                  }
                </SelectValue>
                <svg
                  viewBox='0 0 24 24'
                  fill='none'
                  className={clsx(
                    "tw-flex-none tw-transition",
                    variant === "pop-image" ? "tw-size-[1.125rem]" : "tw-size-4",
                    isOpen || isHovered ? "tw-text-black" : "tw-text-gray-400",
                  )}
                  aria-hidden
                >
                  <path
                    d='M8 10L11.2929 13.2929C11.6834 13.6834 12.3166 13.6834 12.7071 13.2929L16 10'
                    stroke='currentColor'
                    strokeWidth='2.25'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </>
            )}
          </Button>
          <ListBox
            items={items ?? []}
            offset={variant === "pop-image" ? 4 : 0}
            crossOffset={-4}
            virtualize={virtualize}
            className={listBoxClassName}
            style={listBoxStyle}
            scrollTo={scrollTo}
          />
        </>
      )}
    </RACSelect>
  );
});

export { SelectRedesign as Select };
